import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { apiEndpoint } from "../config/api";
import { apiFetch } from "../services/api";
import {
  rollupFranchises,
  summarizeFranchises,
  STATUS_META,
} from "../utils/franchiseLifecycleRollup";

// Franchise Inventory — READ-ONLY stewardship surface (design
// docs/projects/franchise-lifecycle-surfaces-design.md). One row per warehouse-KNOWN franchise: the union
// of currently LK/SI-flagged companies AND flag-off, data-retained formers that Onboarding Candidates
// cannot see. It answers "understand & administer the whole franchise population", where Onboarding
// Candidates answers "what should I be onboarding?". Both project the SAME shared factual lifecycle read
// (getFranchiseLifecycle); here the LK/SI flag is a VARYING column (its role-flip is the clearest signal
// these are two jobs, not one), and status is derived client-side by franchiseLifecycleRollup (adds
// "Former"). Advisory, read-only; configures nothing.

// Chip presentation per status (labels come from STATUS_META; colour/variant are presentation-only).
const STATUS_CHIP = {
  not_onboarded: { color: "warning", variant: "filled" },
  no_eligibility: { color: "default", variant: "outlined" },
  onboarded: { color: "success", variant: "outlined" },
  former: { color: "default", variant: "outlined" },
};

// Display truthiness for the raw 0/1 facts (see franchiseLifecycleRollup for the same guard).
const yes = (v) => v === 1 || v === true || v === "1";

// Column headers + on-page definitions (header tooltips). Order MUST match the body cells in tableRows.
// The isLKSI column is what distinguishes this view from Onboarding Candidates — there the flag is a
// constant filter (=1), here it varies and is meaningful (flag-off rows are the formers).
const COLUMNS = [
  { label: "Com Code", align: "left", tip: "The franchise's normalized Dash company code (com_code)." },
  { label: "Franchise Name", align: "left", tip: "Company name (curated discovery model for flagged franchises, Dash companymeta mirror for formers)." },
  { label: "LK/SI", align: "center", tip: "The current Dash isLKSI flag (islilkickerclient). Yes = flagged an LK/SI franchise now; No = flag off — a former whose warehouse history is retained. Authoritative from dim_franchise, which carries the flag for every company." },
  { label: "Status", align: "left", tip: "Derived state (not stored): Not onboarded (flagged, no warehouse data) → No eligibility period (data present, no LK/SI eligibility period) → Onboarded (data + eligibility) → Former (flag off, historical data retained). Advisory — the flag is self-reported and may be stale in either direction." },
  { label: "Warehouse Data", align: "center", tip: "Whether operational data has landed — any dim_season / dim_facility rows for this franchise. A PROXY for warehouse extraction/transform config, not the config state itself (incremental dims retain rows after a franchise is retired)." },
  { label: "Class Seasons", align: "right", tip: "Count of class-type seasons in the warehouse for this franchise (dim_season, season_type = 'c')." },
  { label: "Eligibility", align: "center", tip: "Whether this franchise has an LK/SI eligibility period configured (app.lksi_franchise_enablement_period). 'Configured' = a period exists; '—' = none. Separate from the isLKSI flag and from warehouse data." },
  { label: "First Seen", align: "left", tip: "createDate from Dash companymeta (via the discovery model) — available for flagged franchises; blank (—) for formers, which the mirror does not carry a create date for." },
  { label: "Last Synced", align: "left", tip: "Last time this company's Dash record changed (Fivetran sync) — a metadata-freshness proxy, NOT season/operational activity." },
  { label: "Com ID", align: "right", tip: "Dash company id (comid); disambiguates duplicate com_codes." },
];

export default function FranchiseInventory() {
  const [franchises, setFranchises] = useState([]);
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");

  const load = useCallback(() => {
    return apiFetch(`${apiEndpoint}/franchise-inventory`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setFranchises(data.franchises || []);
        setError(false);
        setLoaded(true);
      })
      .catch((e) => {
        console.log("franchise-inventory failed:", e);
        setError(true);
        setLoaded(true);
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => rollupFranchises(franchises), [franchises]);
  const summary = useMemo(() => summarizeFranchises(rows), [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        String(r.com_code).toLowerCase().includes(q) ||
        String(r.franchise_name || "").toLowerCase().includes(q)
    );
  }, [rows, query]);

  const summaryChips = [
    { label: "Franchises", value: summary.total },
    { label: "Not onboarded", value: summary.notOnboarded },
    { label: "No eligibility period", value: summary.noEligibility },
    { label: "Onboarded", value: summary.onboarded },
    { label: "Former", value: summary.former },
  ].map((s) => (
    <Chip key={s.label} size="small" variant="outlined" label={`${s.label}: ${s.value}`} />
  ));

  const tableRows = filtered.map((r) => {
    const chip = STATUS_CHIP[r.status];
    const subdued = r.status === "former"; // historical rows recede; the active fleet stays legible
    return (
      <TableRow key={r.com_code} sx={subdued ? { opacity: 0.55 } : undefined}>
        <TableCell>{r.com_code}</TableCell>
        <TableCell>{r.franchise_name}</TableCell>
        <TableCell align="center">{yes(r.is_lksi) ? "Yes" : "No"}</TableCell>
        <TableCell>
          <Chip size="small" color={chip.color} variant={chip.variant} label={STATUS_META[r.status].label} />
        </TableCell>
        <TableCell align="center">{yes(r.warehouse_data_found) ? "Yes" : "No"}</TableCell>
        <TableCell align="right">{r.season_count ?? 0}</TableCell>
        <TableCell align="center">{yes(r.has_eligibility) ? "Configured" : "—"}</TableCell>
        <TableCell>{r.created_date || "—"}</TableCell>
        <TableCell>{r.last_synced || "—"}</TableCell>
        <TableCell align="right">{r.com_id ?? "—"}</TableCell>
      </TableRow>
    );
  });

  const headerCells = COLUMNS.map((c) => (
    <TableCell key={c.label} align={c.align}>
      <Tooltip title={c.tip} arrow placement="top">
        <span style={{ cursor: "help", textDecoration: "underline dotted", textUnderlineOffset: 3 }}>
          {c.label}
        </span>
      </Tooltip>
    </TableCell>
  ));

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Every franchise Season Manager knows from the warehouse — currently LK/SI-flagged companies{" "}
        <strong>and</strong> flag-off <strong>formers</strong> whose historical data is retained for
        reporting. Use this to understand and administer the whole franchise population; the{" "}
        <strong>LK/SI</strong> column shows which are flagged now. This view is advisory and read-only;
        it configures nothing. For the focused "what should I be onboarding?" queue, see Onboarding
        Candidates.
      </Typography>

      {error ? (
        <Typography variant="body2" color="error">
          Unable to load franchise inventory. Please try again later.
        </Typography>
      ) : !loaded ? (
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No franchises to display.</Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5, alignItems: "center" }}>
            {summaryChips}
            <Box sx={{ flexGrow: 1 }} />
            <TextField
              size="small"
              placeholder="Filter by com code or name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ minWidth: 240 }}
            />
          </Box>
          <Paper variant="outlined" sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>{headerCells}</TableRow>
              </TableHead>
              <TableBody>{tableRows}</TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
}
